import React, { useState, useRef, useEffect } from "react";
import { Play, Pause, RotateCcw } from "lucide-react";
import { Select } from "../ui/Select";
import MenuSelect from "../ui/MenuSelect";
import Tooltip from "../ui/Tooltip";
import { logger } from "../../utils/logger";

const AnimationPlayer = ({
  keyframeNames = [],
  interpolateKeyframes,
  handleKeyframeSelect,
  isPlaying,
  setIsPlaying,
  currentTime,
  setCurrentTime,
  setCurrentKeyframeIndex,
  setInterpolationProgress,
  loopAnimation,
  showProgressBar = false,
  useMenuStyle = false,
}) => {
  const [animationSpeed, setAnimationSpeed] = useState(1.0);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(0);
  const currentTimeRef = useRef(0);
  const isPlayingRef = useRef(false);

  const keyframeCount = keyframeNames.length;

  // Calculate progress percentage
  const totalDuration = keyframeCount * 2; // 2 seconds per keyframe
  const cycleTime = loopAnimation
    ? currentTime % totalDuration
    : Math.min(currentTime, totalDuration);
  const progressPercentage =
    keyframeCount > 0 ? Math.min((cycleTime / totalDuration) * 100, 100) : 0;

  // Animation logic
  const startAnimation = () => {
    if (keyframeCount === 0) return;

    setIsPlaying(true);
    isPlayingRef.current = true;
    lastTimeRef.current = performance.now();
    // Do NOT reset currentTimeRef.current or setCurrentTime here
    // Only reset if you want to start from the beginning (e.g., in resetAnimation)
    // setCurrentKeyframeIndex(0); // Don't reset index here
    // setInterpolationProgress(0); // Don't reset progress here

    // Don't immediately apply the first keyframe - let the interpolation handle it
    // This allows smooth transition from current position to first keyframe

    const animate = (currentTime) => {
      if (!isPlayingRef.current) return;

      const deltaTime = (currentTime - lastTimeRef.current) / 1000;
      lastTimeRef.current = currentTime;

      // Apply speed factor to the delta time
      const adjustedDeltaTime = deltaTime * animationSpeed;
      currentTimeRef.current += adjustedDeltaTime;

      const totalDuration = keyframeCount * 2; // 2 seconds per keyframe

      // Handle looping or stopping - check current loopAnimation state
      if (currentTimeRef.current >= totalDuration) {
        if (loopAnimation) {
          // For looping, don't reset to 0 - let the modulo operation handle it naturally
          // This ensures smooth transitions
          logger.info("[AnimationPlayer] Loop cycle completed - continuing with modulo");
        } else {
          // Stop animation
          setIsPlaying(false);
          isPlayingRef.current = false;
          currentTimeRef.current = totalDuration;
          setCurrentTime(totalDuration);
          logger.info("[AnimationPlayer] Animation stopped at end");
          return; // Exit the animation loop
        }
      }

      // Update the state for UI display
      setCurrentTime(currentTimeRef.current);

      // Continue animation loop
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start the animation loop
    animationRef.current = requestAnimationFrame(animate);
  };

  const stopAnimation = () => {
    setIsPlaying(false);
    isPlayingRef.current = false;
    setCurrentKeyframeIndex(null);
    setInterpolationProgress(0);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  const resetAnimation = () => {
    stopAnimation();
    currentTimeRef.current = 0;
    setCurrentTime(0);
    // Reset to the first user keyframe instead of default
    if (keyframeNames.length > 0) {
      handleKeyframeSelect(keyframeNames[0]);
    }
  };

  // Apply interpolated keyframe based on current time
  useEffect(() => {
    if (!isPlaying || keyframeCount === 0) return;

    const totalDuration = keyframeCount * 2; // 2 seconds per keyframe

    // Calculate the current position in the animation cycle
    let cycleTime;
    if (loopAnimation) {
      // For looping, wrap the time around the total duration
      cycleTime = currentTime % totalDuration;
    } else {
      // For non-looping, clamp to the total duration
      cycleTime = Math.min(currentTime, totalDuration);
    }

    const normalizedTime = cycleTime / totalDuration;

    // Get the current keyframe index and interpolation factor
    const keyframeIndex = Math.floor(normalizedTime * keyframeCount);
    const nextKeyframeIndex = (keyframeIndex + 1) % keyframeCount;
    const interpolationFactor = (normalizedTime * keyframeCount) % 1;

    // Update visual feedback
    setCurrentKeyframeIndex(keyframeIndex);
    setInterpolationProgress(interpolationFactor);

    // Get the keyframe names
    const currentKeyframeName = keyframeNames[keyframeIndex];
    const nextKeyframeName = keyframeNames[nextKeyframeIndex];

    // Only use 'current' for the very first frame after play (not looping)
    const isFirstFrame =
      currentTime === 0 && keyframeIndex === 0 && interpolationFactor === 0 && !loopAnimation;

    if (interpolateKeyframes && currentKeyframeName && nextKeyframeName) {
      const clampedFactor = Math.max(0, Math.min(1, interpolationFactor));
      if (isFirstFrame) {
        // Only for the very first frame after play
        interpolateKeyframes("current", currentKeyframeName, clampedFactor);
      } else {
        // For all other frames, always interpolate between actual keyframes
        interpolateKeyframes(currentKeyframeName, nextKeyframeName, clampedFactor);
      }
    } else {
      handleKeyframeSelect(currentKeyframeName);
    }
  }, [
    currentTime,
    isPlaying,
    keyframeCount,
    keyframeNames,
    handleKeyframeSelect,
    interpolateKeyframes,
    setCurrentKeyframeIndex,
    setInterpolationProgress,
    loopAnimation,
  ]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Speed options for dropdown
  const speedOptions = [
    { value: 0.25, label: "0.25x" },
    { value: 0.5, label: "0.5x" },
    { value: 1.0, label: "1x" },
    { value: 1.5, label: "1.5x" },
    { value: 2.0, label: "2x" },
  ];

  return (
    <div className="p-3 rounded border border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium">Controls</span>
        <div className="flex items-center gap-1">
          <Tooltip content={isPlaying ? "Pause Animation" : "Play Animation"}>
            <button
              onClick={isPlaying ? stopAnimation : startAnimation}
              disabled={keyframeCount === 0}
              className={`p-1.5 rounded transition-colors duration-150 ${
                keyframeCount === 0
                  ? "panel-text-helper cursor-not-allowed"
                  : "text-gray-300 hover:text-white hover:bg-gray-700"
              }`}
              aria-label={isPlaying ? "Pause Animation" : "Play Animation"}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
          </Tooltip>
          <Tooltip content="Reset to First Keyframe">
            <button
              onClick={resetAnimation}
              className="p-1.5 rounded text-gray-300 hover:text-white hover:bg-gray-700 transition-colors duration-150"
              aria-label="Reset to First Keyframe"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Progress Bar */}
      {showProgressBar && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400">Progress</span>
            <span className="text-xs text-gray-400">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-150 ease-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Speed Control */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400">Speed</span>
        {useMenuStyle ? (
          <Select
            value={animationSpeed}
            onChange={setAnimationSpeed}
            options={speedOptions}
            className="w-20"
          />
        ) : (
          <MenuSelect
            size="compact"
            className="w-20"
            value={String(animationSpeed)}
            onChange={(v) => setAnimationSpeed(parseFloat(v))}
            options={speedOptions.map((o) => ({ value: String(o.value), label: o.label }))}
            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
            aria-label="Animation speed"
          />
        )}
      </div>
    </div>
  );
};

export default AnimationPlayer;
